/** @file Builds overlay triangles (bones, names, mesh, weights, onion, grid, ik, trails, diff) into chunks. */
import type { RenderBuffers } from '@pose2d/geometry/types';
import { createSink, type Sink } from './draw/createSink';
import { finishSink } from './draw/finishSink';
import { drawLabels } from './labels/drawLabels';
import type { LabelRequest } from './labels/placeLabels';
import { collectNames } from './layers/collectNames';
import { drawAxes } from './layers/drawAxes';
import { drawBones } from './layers/drawBones';
import { drawFootprint } from './layers/drawFootprint';
import { drawGhosts } from './layers/drawGhosts';
import { drawGrid } from './layers/drawGrid';
import { drawIk } from './layers/drawIk';
import { drawMesh } from './layers/drawMesh';
import { drawSockets } from './layers/drawSockets';
import { drawTrails } from './layers/drawTrails';
import { drawWeights } from './layers/drawWeights';
import type { Layer } from './layers/layer';
import { meshScratch } from './layers/meshScratch';
import type { OverlayInput } from './overlayInput';

/** What a build left out; buildOverlay fills it when the caller passes one. */
export interface OverlayReport {
  /**
   * Visible labels dropped because their layer (names, vertex ids) already placed `OVERLAY_STYLE.maxLabelsPerLayer`:
   * a layer keeps its earliest requests (names: bones, slots in draw order, sockets; vertex ids: meshes in draw
   * order, vertices by index) and drops the rest. 0 when nothing was dropped.
   */
  droppedLabels: number;
}

/** Bottom to top. collectNames draws nothing: it queues name labels first so they win placement. */
const LAYERS: Layer[] = [
  collectNames, drawGrid, drawFootprint, drawAxes, drawGhosts, drawTrails, drawWeights, drawMesh, drawBones, drawIk, drawSockets,
];

/** Label requests of the current build and, per layer, the end of its requests (scratch reused across builds). */
const labels: LabelRequest[] = [];
const ends: number[] = [];

/** Runs the layers and draws their labels; returns how many labels the per-layer cap dropped. */
function drawAll(input: OverlayInput, sink: Sink): number {
  labels.length = 0;
  ends.length = 0;
  for (let k = 0; k < LAYERS.length; k++) {
    LAYERS[k]?.(sink, input, labels);
    ends.push(labels.length);
  }
  return drawLabels(sink, labels, ends);
}

/**
 * Fills as many chunks as needed (allocating via `input.allocate`) and returns the number used. Mesh buffers past the
 * retained 2 MiB (meshScratch) live only for the build: they are released when it ends, thrown or not.
 */
export function buildOverlay(outChunks: RenderBuffers[], input: OverlayInput, report?: OverlayReport): number {
  const sink = createSink(outChunks, input);
  try {
    const dropped = drawAll(input, sink);
    if (report !== undefined) report.droppedLabels = dropped + sink.droppedLabels;
  } finally {
    labels.length = 0;
    meshScratch.release();
  }
  return finishSink(sink);
}
